<template>
  <div class="row">
    <div v-for="column in columnDefinitions" :key="column.id" :style="{ width: `${column.width}px` }" class="cell">
      <visible :when="column.id === 'status'">
        <ProjectStatusSelect
          :statuses="statuses"
          :value="project.status"
          :disabled="!hasPermission"
          @input="handleProjectStatusChange(project, $event)"
        />
      </visible>
      <visible :when="column.id === 'steps-statuses'" class="steps-statuses">
        <p class="step-status" style="display: none">
          <ts-badge class="badge" size="mini" :type="getStepStatus(project, 'brief')"></ts-badge>
          <router-link :to="{ name: 'project-brief', params: { projectId: project.id } }">BRIEF</router-link>
        </p>
        <p class="step-status" style="display: none">
          <ts-badge class="badge" size="mini" :type="getStepStatus(project, 'production')"></ts-badge>
          <router-link :to="{ name: 'project-production', params: { projectId: project.id } }">
            PRODUCTION
          </router-link>
        </p>
        <p class="step-status">
          <ts-badge class="badge" size="mini" :type="getStepStatus(project, 'delivery')"></ts-badge>
          <router-link :to="{ name: 'project-delivery', params: { projectId: project.id } }"> DELIVERY </router-link>
        </p>
        <p class="step-status" style="display: none">
          <ts-badge class="badge" size="mini" :type="getStepStatus(project, 'documents')"></ts-badge>
          <router-link :to="{ name: 'project-documents', params: { projectId: project.id } }"> DOCUMENTS </router-link>
        </p>
      </visible>
      <visible :when="column.id === 'name'" class="centered">
        <router-link :to="{ name: 'project-delivery', params: { projectId: project.id } }" class="project-link">
          <div>
            <span class="project-name">
              {{ project.name }}
            </span>
            <br />
            <span class="project-brands">{{ formatBrands(project.brands) }}</span>
            <br />
            <span class="project-quote-number">QUOTE NUMBER</span>
          </div>
        </router-link>
      </visible>
      <visible :when="column.id === 'due-date'">
        <router-link :to="{ name: 'project-brief', params: { projectId: project.id } }" class="project-link">
          <div class="centered">
            <span class="due-date">{{ formatDueDate(project.dueDate) }}</span>
          </div>
        </router-link>
      </visible>
      <visible :when="column.id === 'users'">
        <UserPicker
          :can-add="hasPermission"
          :options="users"
          :value="project.users"
          @input="handleProjectUsersChange(project, $event)"
        />
      </visible>
      <visible :when="column.id === 'actions' && hasPermission" class="actions">
        <ts-button
          v-if="project.status === 'delivered'"
          size="small"
          @click="handleProjectStatusChange(project, 'closed')"
        >
          Close project
        </ts-button>
        <ts-button size="small" circle @click="handleMoveClick(project)">
          <Folder />
        </ts-button>
        <ts-button size="small" circle @click="handleEditClick(project)">
          <Pen />
        </ts-button>
        <ts-button v-if="isAdmin" size="small" circle @click="handleDeleteClick(project)">
          <ts-icon icon="trash" size="x-small" color="#6d7587" title="Delete" />
        </ts-button>
      </visible>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import UserPicker from '@/components/inputs/UserPicker.vue';

import projectViewHelpers from '@/mixins/projectViewHelpers';
import ProjectStatusSelect from '@/components/inputs/ProjectStatusSelect.vue';

import Pen from '@/assets/pen.svg';
import Folder from '@/assets/folder.svg';
import Trash from '@/components/the-source-ui/icons/Trash.vue';
export default {
  mixins: [projectViewHelpers],
  name: 'project',
  props: { project: { type: Object, required: true } },
  components: {
    ProjectStatusSelect,
    UserPicker,
    Pen,
    Folder,
    Trash
  },
  computed: {
    ...mapState('workflow', {
      statuses: state => state.statuses.items,
      users: state => state.users.items
    }),
    hasPermission() {
      return this.isAdmin || (this.isProjectManager && this.isOwner);
    },
    isOwner() {
      return this.currentUser.id === this.project.user_id;
    },
    columnDefinitions() {
      return [
        {
          id: 'status',
          name: 'Status',
          width: 140
        },
        {
          id: 'steps-statuses',
          name: 'Steps statuses',
          width: 100
        },
        {
          id: 'name',
          name: 'Name',
          width: 250
        },
        {
          id: 'due-date',
          name: 'Due date',
          width: 140
        },
        {
          id: 'users',
          name: 'Users',
          width: 200
        },
        {
          id: 'actions',
          name: 'Actions',
          width: 200
        }
      ];
    }
  },
  methods: {
    ...mapActions('workflow', [
      'fetchBrands',
      'fetchUsers',
      'fetchMyProjects',
      'updateMyProjectsQuery',
      'updateMyProjectsFilter',
      'updateProjectStatus',
      'deleteProject',
      'exportProjects',
      'updateProjectUsers'
    ]),
    getStepStatus(project, step) {
      const statusDetail = project.statusDetails[step];
      switch (statusDetail) {
        case 'pending':
          return 'error';
        case 'done':
          return 'success';
        default:
          console.error(`Status not found for project ${project.id}: ${step} (was ${statusDetail}`);
          return 'neutral';
      }
    },
    handleQueryChange(query) {
      this.updateMyProjectsQuery({ query });
    },
    handleMoveClick() {
      window.EventBus.$emit('folders.move', { projects: [this.project] });
    },
    handleFilterChange(field, value) {
      this.updateMyProjectsFilter({ field, value });
    },
    handleProjectStatusChange(project, value) {
      this.updateProjectStatus({ project, value });
    },
    handleDeleteClick(project) {
      if (!confirm('Delete this project?')) {
        return;
      }

      this.deleteProject({ project });
    },
    handleProjectUsersChange(project, value) {
      this.updateProjectUsers({ project, value });
    },
    handleCreateProjectClick() {
      this.$router.push({ name: 'create-project' });
    },
    handleEditClick(project) {
      this.$router.push({ name: 'project-details-general-info', params: { projectId: project.id } });
    }
  }
};
</script>
<style lang="scss" scoped>
.mr-4 {
  margin-right: 1rem;
}
.toolbar {
  .top-row {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .title {
      font-family: Roboto;
      font-size: 16px;
      font-weight: 500;
      color: #6d7587;
    }

    .search-bar {
      width: 500px;
    }
  }

  .bottom-row {
    margin-top: 24px;
    display: flex;
    justify-content: space-between;
  }
}

.list {
  padding: 20px 40px;

  .row {
    background: #ebecf0;
    background-image: linear-gradient(-42deg, rgba(0, 0, 0, 0.4) 0%, rgba(255, 255, 255, 0.4) 100%);
    background-blend-mode: soft-light, normal;
    background-clip: padding-box;
    border: 2px solid rgba(255, 255, 255, 0.16);
    box-shadow: -2px -2px 6px 0 rgba(250, 251, 255, 0.48), 2px 2px 6px 0 rgba(166, 171, 189, 0.24);
    border-radius: 16px;

    display: flex;
    margin-bottom: 12px;

    .cell {
      display: flex;
      flex-direction: column;
      align-content: center;
      justify-content: center;
      padding: 12px;

      .centered {
        text-align: center;
      }

      .steps-statuses {
        font-family: Roboto;
        font-size: 8px;
        color: #9aa5b7;

        .step-status {
          display: flex;
          align-items: flex-end;
          margin: 2px 0;

          .badge {
            margin-right: 3px;
          }

          a {
            font-family: Roboto;
            font-size: 8px;
            color: #9aa5b7;
            text-decoration: none;
          }
        }
      }

      .project-name {
        font-family: Roboto;
        font-size: 12px;
        font-weight: 500;
        line-height: 20px;
        color: #000000;
      }

      .project-link {
        font-family: Roboto;
        font-size: 12px;
        font-weight: 500;
        color: #000000;
        text-decoration: none;
      }

      .project-brands {
        font-family: Roboto;
        font-size: 12px;
        color: #6d7587;
      }

      .project-quote-number {
        font-family: Roboto;
        font-size: 8px;
        color: #9aa5b7;
      }

      .due-date {
        font-family: Roboto;
        font-size: 12px;
        text-align: center;
        line-height: 50px;
        display: block;
        color: #6d7587;
      }

      .actions {
        display: flex;
        justify-content: flex-end;
        align-items: center;

        > * {
          margin-left: 12px;
        }
      }
    }
  }
}
</style>
